import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from "react-router-dom";
import {
  AppRemixIcon,
  AppCard,
  AppInput,
} from '@/layout/AppComponents';
import ReactDOMServer from 'react-dom/server';
import axios from 'axios';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import { axiosPath } from "../../../routing/helpers/router";
import NoInfo from '../../../layout/components/helpers/NoInfo';
import colors from '../../../constants/colors';
import SpinnerLoader from '../../../layout/components/helpers/SpinnerLoader';
import UserManagementModuleSidebar from '../../../layout/screenSpecifics/Modules/UserManagement/UserManagementModuleSidebar';
import UserManagementInfoModules from '../../../layout/screenSpecifics/Modules/UserManagement/UserManagementInfoModules';
import Translator from '../../../layout/components/helpers/Translator';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import _ from 'lodash';
import errorHandler from '@/layout/components/error/ErrorHandler';

export default function UserManagementInfoScreen() {
  const { moduleBridgeHex, userName } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const isSidebarOpened = useSelector(
    (state) => state.auth?.settings?.preferences?.sidebar?.isOpened
  );
  const [moduleBridge, setModuleBridge] = useState();
  const [modules, setModules] = useState();
  const [user, setUser] = useState();
  const [currentUser, setCurrentUser] = useState();
  const [isOpen, setIsOpen] = useState([]);

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  const loadInfoUser = useCallback(async () => {
    await axios
      .post(axiosPath("api_r_user_module_userManagement_user_get"), {
        userName,
        moduleBridgeHex,
      })
      .then((r) => {
        if (isMounted.current) {
          setModuleBridge(r.data.moduleBridge);
          setCurrentUser(r.data.currenUser);
          setUser(r.data.user);
          setModules(r.data.modules);
          setIsOpen(r.data.isOpen);
        }
      })
      .catch((e) => dispatch(errorHandler(e, history)));
  }, [userName]);

  useEffect(() => {
    loadInfoUser();
  }, []);

  const handleIsOpen = useCallback(
    (moduleID) => {
      let tempIsOpen = [...isOpen];
      if (tempIsOpen.includes(moduleID)) {
        tempIsOpen = _.filter(tempIsOpen, (o) => o !== moduleID);
      } else {
        tempIsOpen.push(moduleID);
      }
      setIsOpen(tempIsOpen);
    },
    [isOpen]
  );

  const handleExpanseAll = useCallback(
    (state) => {
      let tempIsOpen = [];

      if (state) {
        _.forEach(modules, (module) => {
          tempIsOpen.push(module.id);
        });
      }

      setIsOpen(tempIsOpen);
    },
    [isOpen, modules]
  );

  return (
    <div>
      <BreadcrumbHandler
        paths={
          moduleBridge
            ? [
                {
                  name: moduleBridge?.name,
                  route: {
                    name: 'user_module_userManagement_home',
                    params: { moduleBridgeHex },
                  },
                },
                {
                  name: `${ReactDOMServer.renderToString(
                    <Translator
                      file="module-userManagement"
                      name="module.um.user-information"
                    />
                  )} ${userName}`,
                },
              ]
            : [{ name: '...' }]
        }
      />

      <UserManagementModuleSidebar permissionLevel={user?.permissionLevel} />

      <div className="row">
        <main role="main" className="col-md-12 col-lg-12 px-md-4">
          <div className={`${isSidebarOpened && 'sidebar-expanded'}`}>
            <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-0 mb-0 border-bottom">
              <AppCard
                header={ReactDOMServer.renderToString(
                  <Translator
                    file="module-userManagement"
                    name="module.um.user-information"
                  />
                )}
              >
                {currentUser && modules ? (
                  <>
                    <AppInput
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-userManagement"
                          name="module.um.user"
                        />
                      )}
                      icon={<AppRemixIcon icon="font-color" noVariants />}
                      value={currentUser.user}
                      isReadOnly
                    />
                    <AppInput
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-userManagement"
                          name="module.um.name"
                        />
                      )}
                      icon={<AppRemixIcon icon="user" />}
                      value={currentUser.name}
                      isReadOnly
                    />
                    <AppInput
                      icon={<AppRemixIcon icon="mail" />}
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-userManagement"
                          name="module.um.email"
                        />
                      )}
                      value={currentUser.email}
                      isReadOnly
                    />
                  </>
                ) : (
                  <NoInfo
                    icon={<SpinnerLoader />}
                    text={ReactDOMServer.renderToString(
                      <Translator
                        file="module-userManagement"
                        name="module.um.loading"
                      />
                    )}
                    color={colors.primary}
                  />
                )}
              </AppCard>

              <AppCard
                header={
                  <div>
                    <div className="d-inline-flex">
                      <span className="mt-1">
                        <Translator
                          file="module-userManagement"
                          name="module.um.user-modules"
                        />
                      </span>
                    </div>

                    <span className="float-right ">
                      <AppRemixIcon
                        icon="arrow-down-circle"
                        size={25}
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-userManagement"
                            name="module.um.expanse-all"
                          />
                        )}
                        onClick={() => handleExpanseAll(true)}
                        className="mr-3 pointer text-primary"
                      />
                      <AppRemixIcon
                        icon="arrow-up-circle"
                        size={25}
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-userManagement"
                            name="module.um.contract-all"
                          />
                        )}
                        onClick={() => handleExpanseAll(false)}
                        className="pointer text-primary"
                      />
                    </span>
                  </div>
                }
                className="mt-3"
              >
                {currentUser && modules ? (
                  <>
                    {modules.map((module) => (
                      <UserManagementInfoModules
                        moduleBridge={moduleBridge}
                        module={module}
                        key={module.id}
                        isOpen={isOpen}
                        onOpen={(moduleID) => handleIsOpen(moduleID)}
                      />
                    ))}
                  </>
                ) : (
                  <NoInfo
                    icon={<SpinnerLoader />}
                    text={ReactDOMServer.renderToString(
                      <Translator
                        file="module-userManagement"
                        name="module.um.loading"
                      />
                    )}
                    color={colors.primary}
                  />
                )}
              </AppCard>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
